<template>
  <div class="g-page">
    <div class="content-container">
      <!-- HEADER -->
      <div class="header-row">
        <h2 class="page-title">Mahsulot Brand/Model</h2>
        <div class="header-actions">
          <div class="search-wrapper">
            <b-icon icon="search" class="search-icon"></b-icon>
            <input v-model="search" type="search" placeholder="Qidiruv..." class="modern-search" @input="onSearch" />
          </div>
          <button class="refresh-btn" @click="reset()" title="Yangilash">
            <b-icon icon="arrow-repeat"></b-icon>
          </button>
          <button class="add-btn" :disabled="selected.length === 0" @click="openBulkModal()">
            <b-icon icon="pencil-square"></b-icon> Tahrirlash ({{ selected.length }})
          </button>
        </div>
      </div>

      <!-- TABS -->
      <div class="tabs-row">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="tab-btn"
          :class="{ active: activeTab === tab.key }"
          @click="changeTab(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- SELECT ALL BAR -->
      <div class="select-bar" v-if="list.length > 0">
        <label class="select-all-label">
          <input type="checkbox" :checked="allSelected" @change="toggleAll" />
          <span>Barchani tanlash</span>
        </label>
        <span class="counter-badge">{{ list.length }} / {{ total }}</span>
        <span v-if="selected.length > 0" class="selected-hint">
          {{ selected.length }} ta tanlandi
        </span>
      </div>

      <!-- TABLE -->
      <div class="table-card">
        <div class="table-wrapper" ref="tableWrapper" @scroll="onScroll">
          <table class="custom-table">
            <thead>
              <tr>
                <th style="width:44px"></th>
                <th style="width:55px">#</th>
                <th>Mahsulot nomi</th>
                <th>Umumiy nomi</th>
                <th style="width:160px">Brand</th>
                <th style="width:160px">Model</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, idx) in list"
                :key="item.id"
                :class="{ 'row-selected': isSelected(item.id) }"
                @click="toggleOne(item.id)"
              >
                <td @click.stop>
                  <input type="checkbox" :checked="isSelected(item.id)" @change="toggleOne(item.id)" />
                </td>
                <td><span class="id-badge">{{ idx + 1 }}</span></td>
                <td class="name-cell">{{ item.name }}</td>
                <td class="extra-name-cell">{{ item.extra_name }}</td>
                <td>
                  <span v-if="item.brand" class="tag brand-tag">{{ item.brand.name }}</span>
                  <span v-else class="tag empty-tag">—</span>
                </td>
                <td>
                  <span v-if="item.model" class="tag model-tag">{{ item.model.name }}</span>
                  <span v-else class="tag empty-tag">—</span>
                </td>
              </tr>

              <!-- LOADING ROW -->
              <tr v-if="loadingMore">
                <td colspan="6" class="loading-row">
                  <div class="spinner"></div> Yuklanmoqda...
                </td>
              </tr>

              <!-- EMPTY -->
              <tr v-if="list.length === 0 && !loading && !loadingMore">
                <td colspan="6" class="empty-row">Ma'lumot topilmadi</td>
              </tr>

              <!-- INITIAL LOADING -->
              <tr v-if="list.length === 0 && loading">
                <td colspan="6" class="loading-row">
                  <div class="spinner"></div> Yuklanmoqda...
                </td>
              </tr>
            </tbody>
          </table>

          <!-- BOTTOM SENTINEL — ko'rinsa yangi sahifa yuklanadi -->
          <div ref="sentinel" style="height:1px"></div>
        </div>

        <!-- PAGINATION INFO -->
        <div class="pagination-bar" v-if="total > 0">
          <span>Ko'rsatilmoqda: <b>{{ list.length }}</b> / <b>{{ total }}</b></span>
          <button v-if="hasMore && !loadingMore" class="load-more-btn" @click="loadMore()">
            Yana {{ remaining }} ta yuklash
          </button>
          <span v-if="!hasMore && list.length > 0" class="all-loaded">Hammasi yuklandi ✓</span>
        </div>
      </div>
    </div>

    <!-- BULK EDIT MODAL -->
    <b-modal id="modal-bulk-edit" title="Brand/Model o'zgartirish" hide-footer size="md" modal-class="modern-modal">
      <div class="modal-form">
        <p class="bulk-info">{{ selected.length }} ta mahsulot uchun o'rnatiladi.</p>

        <div class="form-unit">
          <label>Brand</label>
          <select v-model="bulkBrand" class="form-input" @change="bulkModel = undefined">
            <option :value="undefined">— O'zgartirmaslik —</option>
            <option :value="null">— Tozalash (null) —</option>
            <option v-for="b in brands" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
        </div>

        <div class="form-unit">
          <label>Model</label>
          <select v-model="bulkModel" class="form-input">
            <option :value="undefined">— O'zgartirmaslik —</option>
            <option :value="null">— Tozalash (null) —</option>
            <option v-for="m in filteredModels" :key="m.id" :value="m.id">{{ m.name }}</option>
          </select>
        </div>

        <div class="form-unit" v-if="bulkBrand !== undefined || bulkModel !== undefined">
          <div class="change-summary">
            <span v-if="bulkBrand !== undefined">Brand: <b>{{ brandLabel(bulkBrand) }}</b></span>
            <span v-if="bulkModel !== undefined">Model: <b>{{ modelLabel(bulkModel) }}</b></span>
          </div>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn-cancel" @click="$bvModal.hide('modal-bulk-edit')">Bekor</button>
          <button type="button" class="btn-save" @click="saveBulk()" :disabled="saving || (bulkBrand === undefined && bulkModel === undefined)">
            <b-icon icon="check-all" class="mr-1"></b-icon>
            {{ saving ? 'Saqlanmoqda...' : 'Saqlash' }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';

const LIMIT = 50;

export default {
  name: 'ProductBrandModel',
  data() {
    return {
      activeTab: 'no_brand',
      tabs: [
        { key: 'no_brand', label: 'Brandsiz' },
        { key: 'no_model', label: 'Modelsiz' },
        { key: 'has_brand', label: 'Brandli' },
        { key: 'has_model', label: 'Modelli' },
      ],
      list: [],
      total: 0,
      brands: [],
      models: [],
      selected: [],
      search: '',
      loading: false,
      loadingMore: false,
      saving: false,
      bulkBrand: undefined,
      bulkModel: undefined,
      observer: null,
    };
  },
  computed: {
    hasMore() {
      return this.list.length < this.total;
    },
    remaining() {
      return Math.min(LIMIT, this.total - this.list.length);
    },
    allSelected() {
      return this.list.length > 0 && this.selected.length === this.list.length;
    },
    filteredModels() {
      if (this.bulkBrand == null || this.bulkBrand === undefined) return this.models;
      return this.models.filter(m => m.brand_id === this.bulkBrand);
    },
  },
  methods: {
    /* ─── Data loading ─── */
    async fetchPage(offset = 0) {
      const res = await axios.get('api/v1/product/brand-model-list', {
        params: {
          filter: this.activeTab,
          search: this.search || undefined,
          limit: LIMIT,
          offset,
        },
      });
      return res.data; // { total, limit, offset, items }
    },

    async reset() {
      this.list = [];
      this.total = 0;
      this.selected = [];
      this.loading = true;
      try {
        const data = await this.fetchPage(0);
        this.total = data.total;
        this.list  = data.items;
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;
      this.loadingMore = true;
      try {
        const data = await this.fetchPage(this.list.length);
        this.total = data.total;
        this.list.push(...data.items);
      } finally {
        this.loadingMore = false;
      }
    },

    /* ─── Scroll & Intersection Observer ─── */
    setupObserver() {
      if (this.observer) this.observer.disconnect();
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && this.hasMore && !this.loadingMore) {
            this.loadMore();
          }
        },
        { root: this.$refs.tableWrapper, threshold: 0.1 }
      );
      if (this.$refs.sentinel) {
        this.observer.observe(this.$refs.sentinel);
      }
    },

    onScroll() {
      // IntersectionObserver bilan ishlaydi, qo'shimcha manual scroll kerak emas
    },

    /* ─── Tab & Search ─── */
    changeTab(key) {
      this.activeTab = key;
      this.reset();
    },

    onSearch: debounce(function () {
      this.reset();
    }, 400),

    /* ─── Selection ─── */
    isSelected(id) {
      return this.selected.includes(id);
    },
    toggleOne(id) {
      const idx = this.selected.indexOf(id);
      if (idx === -1) this.selected.push(id);
      else this.selected.splice(idx, 1);
    },
    toggleAll() {
      if (this.allSelected) {
        this.selected = [];
      } else {
        this.selected = this.list.map(i => i.id);
      }
    },

    /* ─── Bulk Edit ─── */
    openBulkModal() {
      this.bulkBrand = undefined;
      this.bulkModel = undefined;
      this.$bvModal.show('modal-bulk-edit');
    },

    brandLabel(val) {
      if (val === undefined) return '—';
      if (val === null) return 'Tozalanadi';
      const b = this.brands.find(x => x.id === val);
      return b ? b.name : val;
    },
    modelLabel(val) {
      if (val === undefined) return '—';
      if (val === null) return 'Tozalanadi';
      const m = this.models.find(x => x.id === val);
      return m ? m.name : val;
    },

    async saveBulk() {
      if (this.selected.length === 0) return;
      if (this.bulkBrand === undefined && this.bulkModel === undefined) return;

      const payload = { ids: this.selected };
      if (this.bulkBrand !== undefined) payload.brand_id = this.bulkBrand;
      if (this.bulkModel !== undefined) payload.model_id = this.bulkModel;

      this.saving = true;
      try {
        await axios.patch('api/v1/product/bulk-brand-model', payload);
        this.$bvModal.hide('modal-bulk-edit');
        this.selected = [];
        await this.reset();
      } finally {
        this.saving = false;
      }
    },

    /* ─── Reference data ─── */
    loadBrands() {
      axios.get('api/v1/brand').then(r => { this.brands = r.data; });
    },
    loadModels() {
      axios.get('api/v1/model').then(r => { this.models = r.data; });
    },
  },

  async mounted() {
    await this.reset();
    this.$nextTick(() => this.setupObserver());
    this.loadBrands();
    this.loadModels();
  },

  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  },
};
</script>

<style scoped>
.g-page {
  padding: 20px;
  height: 100%;
  overflow-y: auto;
}

.content-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* HEADER */
.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  flex-wrap: wrap;
  gap: 12px;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 14px;
}

.modern-search {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 9px 14px 9px 36px;
  border-radius: 10px;
  width: 220px;
  outline: none;
  font-size: 14px;
  transition: 0.2s;
}

.modern-search:focus {
  border-color: #a142f4;
  box-shadow: 0 0 0 3px rgba(161,66,244,0.08);
  background: white;
}

.refresh-btn {
  background: #f1f5f9;
  border: none;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: 0.2s;
}

.refresh-btn:hover { background: #e2e8f0; }

.add-btn {
  background: linear-gradient(135deg, #a142f4 0%, #8b2cf5 100%);
  color: white;
  border: none;
  height: 38px;
  padding: 0 18px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(161,66,244,0.25);
  transition: 0.2s;
}

.add-btn:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; }
.add-btn:not(:disabled):hover { transform: translateY(-1px); }

/* TABS */
.tabs-row {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.tab-btn {
  padding: 8px 20px;
  border-radius: 10px;
  border: 2px solid transparent;
  background: #f1f5f9;
  color: #64748b;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
}

.tab-btn.active {
  background: white;
  border-color: #a142f4;
  color: #a142f4;
  box-shadow: 0 2px 8px rgba(161,66,244,0.15);
}

.tab-btn:not(.active):hover { background: #e9d5ff; color: #7c3aed; }

/* SELECT BAR */
.select-bar {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  border-radius: 10px;
  padding: 8px 16px;
  margin-bottom: 10px;
}

.select-all-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 600;
  color: #7c3aed;
  font-size: 14px;
  margin: 0;
}

.counter-badge {
  background: #e9d5ff;
  color: #7c3aed;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 20px;
}

.selected-hint {
  font-size: 13px;
  color: #a142f4;
  font-weight: 700;
  margin-left: auto;
}

/* TABLE */
.table-card {
  background: white;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
  max-height: calc(100vh - 310px);
  overflow-y: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
}

.custom-table thead {
  position: sticky;
  top: 0;
  z-index: 2;
}

.custom-table th {
  background: #f8fafc;
  padding: 12px 14px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}

.custom-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #f8fafc;
  font-size: 14px;
  color: #334155;
  vertical-align: middle;
  cursor: pointer;
}

.custom-table tr:hover td { background: #faf5ff; }
.row-selected td { background: #f3e8ff !important; }

.id-badge {
  background: #f1f5f9;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.name-cell { font-weight: 700; color: #1e293b; }
.extra-name-cell { color: #64748b; font-size: 13px; }

.tag {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
}

.brand-tag { background: #dbeafe; color: #1d4ed8; }
.model-tag { background: #dcfce7; color: #15803d; }
.empty-tag { color: #cbd5e1; font-size: 18px; font-weight: 400; }

.loading-row {
  text-align: center;
  padding: 20px !important;
  color: #a142f4;
  font-size: 14px;
}

.empty-row {
  text-align: center;
  padding: 50px !important;
  color: #94a3b8;
  font-style: italic;
}

/* SPINNER */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #e9d5ff;
  border-top-color: #a142f4;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 6px;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* PAGINATION BAR */
.pagination-bar {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 18px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #64748b;
}

.load-more-btn {
  background: #f3e8ff;
  color: #7c3aed;
  border: none;
  padding: 6px 16px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: 0.2s;
}

.load-more-btn:hover { background: #e9d5ff; }

.all-loaded {
  color: #22c55e;
  font-weight: 700;
  font-size: 13px;
}

/* MODAL */
.modal-form { padding: 8px; }

.bulk-info {
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  border-radius: 8px;
  padding: 10px 14px;
  color: #7c3aed;
  font-weight: 600;
  margin-bottom: 18px;
  font-size: 14px;
}

.form-unit { margin-bottom: 18px; }

.form-unit label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  margin-bottom: 7px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-input {
  width: 100%;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;
  color: #1e293b;
  font-size: 14px;
  font-weight: 500;
  outline: none;
  transition: 0.2s;
}

.form-input:focus {
  border-color: #a142f4;
  background: white;
  box-shadow: 0 0 0 3px rgba(161,66,244,0.08);
}

.change-summary {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 14px;
  color: #475569;
  background: #f8fafc;
  padding: 10px 14px;
  border-radius: 8px;
}

.change-summary b { color: #1e293b; }

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
}

.btn-cancel {
  background: #f1f5f9;
  color: #64748b;
  border: none;
  padding: 9px 20px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
}

.btn-cancel:hover { background: #e2e8f0; }

.btn-save {
  background: linear-gradient(135deg, #a142f4 0%, #8b2cf5 100%);
  color: white;
  border: none;
  padding: 9px 24px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(161,66,244,0.25);
  transition: 0.2s;
}

.btn-save:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; }
.btn-save:not(:disabled):hover { transform: translateY(-1px); }
</style>
